import { ApplicationRef, ComponentRef, EnvironmentInjector, Injectable, Injector, Type, createComponent } from '@angular/core';

import { ModalRef } from '@core-lib/modules/modal-container';
import { IModalOptions } from '../models/modal.model';
import { ModalContainerComponent } from '../components/modal-container.component';
import { OverlayContainer } from '@angular/cdk/overlay';

@Injectable({ providedIn: 'root' })
export class ModalService {
    private modals: { id: number; containerRef: ComponentRef<ModalContainerComponent> }[] = [];
    private lastId = 0;

    private baseModalZIndex = 1040;
    private baseBackdropZIndex = 1030;

    constructor(
        private appRef: ApplicationRef,
        private injector: Injector,
        private envInjector: EnvironmentInjector,
        private overlayContainer: OverlayContainer
    ) { }

    show<T>(component: Type<T>, options: IModalOptions = {}): ModalRef {
        const id = ++this.lastId;
        const modalRef = new ModalRef(() => this.hide(id));
        modalRef.id = id;

        const mergedOptions: IModalOptions = {
            backdrop: true,
            keyboard: true,
            ignoreBackdropClick: false,
            ...options
        };

        const containerRef = createComponent(ModalContainerComponent, {
            environmentInjector: this.envInjector,
            elementInjector: Injector.create({
                providers: [{ provide: ModalRef, useValue: modalRef }],
                parent: this.injector
            })
        });

        const containerInstance = containerRef.instance;
        containerInstance.options = mergedOptions;
        containerInstance.modalRef = modalRef;
        containerInstance.childComponentType = component;

        const compRef = containerInstance.vc.createComponent(component, {
            injector: containerRef.injector,
            environmentInjector: this.envInjector
        });
        modalRef.content = compRef.instance;

        if (mergedOptions.initialState) {
            Object.assign(compRef.instance, mergedOptions.initialState);
        }

        // Attach modal to DOM
        this.appRef.attachView(containerRef.hostView);
        document.body.appendChild(containerRef.location.nativeElement);
        //this.appRef.tick();
        containerRef.changeDetectorRef.detectChanges();

        this.modals.push({ id, containerRef });

        this.applyZIndex(containerRef);

        if (this.modals.length === 1) {
            document.body.classList.add('modal-open');
        }

        modalRef.onShow.next();
        modalRef.onShown.next();

        return modalRef;
    }

    // apply stacking
    private applyZIndex(containerRef: ComponentRef<ModalContainerComponent>) {
        const index = this.modals.length - 1;

        const modalZ = this.baseModalZIndex + index * 20;
        const backdropZ = this.baseBackdropZIndex + index * 20;
        this.syncOverlayZIndex(modalZ);
        // this.syncNgSelectZIndex(modalZ);
        const el = containerRef.location.nativeElement;

        const backdrop = el.querySelector('.modal-backdrop');
        if (backdrop) backdrop.style.zIndex = `${backdropZ}`;

        const modal = el.querySelector('.modal');
        if (modal) modal.style.zIndex = `${modalZ}`;
    }

    // apply stackin on material angular compo
    private syncOverlayZIndex(modalZ: number) {
        const overlayEl = this.overlayContainer.getContainerElement();
        overlayEl.style.zIndex = `${modalZ + 1}`;
    }

    // menu deroulant ng-select
    // private syncNgSelectZIndex(modalZ: number) {
    //   const panels = document.querySelectorAll('.ng-dropdown-panel');
    //   panels.forEach((panel: HTMLElement) => {
    //     panel.style.zIndex = `${modalZ + 1}`;
    //   });
    // }

    hide(id?: number): void {
        if (id == null) {
            id = this.modals[this.modals.length - 1]?.id;
        }
        if (!id) return;

        const entry = this.modals.find(m => m.id === id);
        if (!entry) return;

        const { containerRef } = entry;

        entry.containerRef.instance.modalRef.onHide.next();

        this.modals = this.modals.filter(m => m.id !== id);

        if (this.modals.length === 0) {
            document.body.classList.remove('modal-open');
        }

        setTimeout(() => {
            this.appRef.detachView(containerRef.hostView);
            containerRef.destroy();
            entry.containerRef.instance.modalRef.onHidden.next();
            this.modals.forEach((item, idx) => this.applyZIndex(item.containerRef));
        }, 300);
    }
}
